import {Injectable} from '@angular/core';
import {ApiService} from './api.service';
import {CommonUtils} from '../utils/common';
import {Observable} from 'rxjs';
import {VetineoApiResponse} from '../models/vetineo-api-response.model';
import {map, take} from 'rxjs/operators';
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {VMessageDgSearchResults} from "../models/datagrid-search-results/v-message.dg-search-results.model";
import {VMessage} from "../models/v-message.model";
import {VImage} from "../models/vet-clinic.model";

/**
 * Serwis warstwy danych obsługujący wbudowany, bezpieczny system wiadomości (Secure Messaging).
 * Zarządza skrzynką odbiorczą/nadawczą, obsługuje potwierdzenia odczytu oraz
 * wiąże konwersacje z konkretnymi rezerwacjami medycznymi.
 */
@Injectable({
  providedIn: 'root'
})
export class VMessageService implements DataGridServiceInterface<Observable<VMessageDgSearchResults>> {

  constructor(
    private apiService: ApiService,
    public commonUtils : CommonUtils,
  ) {
  }

  /**
   * Pobiera paginowaną listę wiadomości w celu
   * zasilenia widoku tabelarycznego (DataGrid).
   *
   * @param limit Maksymalna liczba rekordów na stronę.
   * @param offset Przesunięcie wyników względem początku zbioru.
   * @param sortOrder Kierunek sortowania (np. 'DESC' dla najnowszych na górze).
   * @param orderBy Nazwa kolumny sortującej (najczęściej data wysłania).
   * @param showDeletedObjects Flaga decydująca o widoczności zarchiwizowanych/usuniętych wiadomości.
   * @returns Observable emitujący ustandaryzowany obiekt wyników dla komponentu DataGrid.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false,): Observable<VMessageDgSearchResults> {


    return this.apiService.get<VetineoApiResponse>(`/dashboard/v-messages/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        const msgResults = new VMessageDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new VMessage(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
        return msgResults;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wykonuje filtrowanie i wyszukiwanie wiadomości w oparciu o zaawansowane parametry.
   *
   * @param limit Rozmiar strony.
   * @param offset Przesunięcie.
   * @param searchFieldsData Mapa zawierająca zdefiniowane filtry wyszukiwania.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Nazwa kolumny sortującej.
   * @param showDeletedObjects Flaga widoczności usuniętych rekordów.
   * @returns Observable emitujący przefiltrowaną listę wiadomości dla DataGrid.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<VMessageDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects,
    }

    return this.apiService.post<VetineoApiResponse>('/dashboard/v-messages/search', data).pipe(take(1), map(vetineoApiResponse => {
      console.log(vetineoApiResponse);
      if (vetineoApiResponse.ok) {
        return new VMessageDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new VMessage(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wysyła nową wiadomość do odbiorcy.
   *
   * @param vMessageToSend Zinstancjonowany obiekt wiadomości gotowy do wysłania.
   * @returns Observable potwierdzający poprawność operacji ciągiem znaków 'OK'.
   */
  send(vMessageToSend: VMessage): Observable<string> {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/v-messages`, vMessageToSend).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return 'OK';
      } else {
        if (vetineoApiResponse.errors) {
           throw new Error(vetineoApiResponse.errors.main);
        }
      }
    }));
  }

  /**
   * Pobiera informacje `VMessage` potrzebne do otworzenia wiadomości.
   *
   * @param uuid Unikalny identyfikator otwieranej wiadomości.
   * @returns Observable emitujący zaktualizowany obiekt wiadomości (ze zmienionym statusem).
   */
  openMessage(uuid: string): Observable<VMessage> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/v-messages/open/${uuid}`,).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<VMessage>(new VMessage(), vetineoApiResponse.response);
      } else {
        if (vetineoApiResponse.errors) {
          return undefined;
        }
      }
    }));
  }

  /**
   * Pobiera wszystkie wiadomości wymienione w kontekście konkretnej rezerwacji.
   *
   * @param uuid Identyfikator nadrzędnej rezerwacji (VetReservation).
   * @returns Observable emitujący tablicę powiązanych wiadomości.
   */
  getForReservationUUID(uuid: string): Observable<VMessage[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/v-messages/by-reservation/${uuid}`,).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VMessage(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera domyślną grafikę/awatar reprezentujący placówkę weterynaryjną.
   * Używana w funkcji "Użyj zdjęcia z lecznicy" przy tworzeniu wiadomości.
   *
   * @returns Observable emitujący domyślny obiekt obrazu (VImage).
   */
  getDefaultClinicImg(): Observable<VImage> {
    return this.apiService.get<VetineoApiResponse>('/dashboard/v-messages/default-img',).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<VImage>(new VImage(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }
}
